import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { OverviewButton } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/overview-button";
import { DefaultComponentMap } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/component-map";
import { UaaExtendedUser, UaaOidcUser, UaaUser } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon/main/icon.view";
import { SizeContext } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/size-detector";
import { QuickAccessButton } from "@com.mgmtp.a12.widgets/widgets-core/lib/quick-access-button/main/quick-access-button.view";

import { Manifest, RuntimeModuleRoleSpec } from "../app/slices/manifest/types";
import { exportApplicationData } from "../modules/processOverview/store/actions";
import {
	getAllowedScopes,
	getUserRoles,
	isAdmin,
	isAllowedRuntimeModule,
	isUser,
	selectHasNeededPermissions,
	User
} from "../utilities/user";
import { RUNTIME_MODULE_CHECK_ROLES } from "../utilities/variables/modulfRuntimeAnnotations";
import { hasUpdateDocumentPermission } from "../utilities/permissionUtils";

const EXPORT_QUICK_ACCESS_BUTTON_STYLE = { backgroundColor: "#d6e4ee" };

export function CustomOverviewButton(user: UaaUser | UaaOidcUser | UaaExtendedUser, manifest?: Manifest) {
	return function CustomOverviewButton(props: OverviewButton.Props) {
		const { currentSize } = React.useContext(SizeContext);
		const mobile = ["xs", "sm"].includes(currentSize ?? "");
		const hasUpdatePermission = useSelector(
			selectHasNeededPermissions(hasUpdateDocumentPermission, manifest?.identification.key)
		);
		if (!manifest) {
			return <DefaultComponentMap.OverviewButton {...props} />;
		}

		const checkRolesAnnotation = props.buttonModel.annotations?.find(e => e.name === RUNTIME_MODULE_CHECK_ROLES);
		if (checkRolesAnnotation?.value && !isAllowedRuntimeModule(user, checkRolesAnnotation?.value, manifest)) {
			return null;
		}

		if (
			props.buttonModel.event === "open_invitations" &&
			!(manifest.persoenlicheLinksVersenden && hasUpdatePermission)
		) {
			return null;
		}

		const dataExportModule = manifest.runtimeModules?.find(e => e.name === "DatenExport");
		if (isExportButton(props) && isUser(user) && dataExportModule) {
			return ExportOverviewButton(
				user,
				manifest,
				dataExportModule.roles ?? [],
				mobile,
				props.buttonModel.event
			)(props);
		}

		return <DefaultComponentMap.OverviewButton {...props} />;
	};
}

function isExportButton(props: OverviewButton.Props) {
	return props.buttonModel.event === "export" || props.buttonModel.event === "exportAnonym";
}

export function ExportOverviewButton(
	user: User,
	manifest: Manifest,
	runtimeModuleRoles: RuntimeModuleRoleSpec[],
	mobile: boolean,
	event: string
): (props: OverviewButton.Props) => React.ReactElement | null {
	return function ExportOverviewButton(props): React.ReactElement | null {
		const dispatch = useDispatch();
		const userRoles = getUserRoles(user, manifest);
		const isAdminUser = isAdmin(user);
		const allowedScopes =
			isAdminUser || userRoles.length === 0 ? [] : getAllowedScopes(userRoles, runtimeModuleRoles);

		const actionItems = React.useMemo(() => {
			return [
				{
					text: "Alle Vorgänge exportieren",
					graphic: <Icon>import_export</Icon>,
					secondaryText: "Vollständig",
					onClick: () => {
						dispatch(exportApplicationData({ manifest, anonym: false }));
					},
					meta: <Icon>done</Icon>
				},
				{
					text: "Alle Vorgänge exportieren",
					graphic: <Icon>import_export</Icon>,
					secondaryText: "Anonymisiert",
					onClick: () => {
						dispatch(exportApplicationData({ manifest, anonym: true }));
					}
				}
			];
		}, [manifest, dispatch]);

		const isFullExportAllowed = !allowedScopes || isAdminUser || allowedScopes.some(e => e.name === "FULL_EXPORT");
		if (event === "export" && isFullExportAllowed) {
			if (!allowedScopes || mobile) {
				return <DefaultComponentMap.OverviewButton {...props} />;
			}

			return (
				<QuickAccessButton
					mainAction={<DefaultComponentMap.OverviewButton {...props} />}
					style={EXPORT_QUICK_ACCESS_BUTTON_STYLE}
					actionItems={actionItems}
				/>
			);
		}

		const isAnonymExportAllowed =
			isAdminUser || isFullExportAllowed || allowedScopes?.some(e => e.name === "ANONYM_EXPORT");
		if (event === "exportAnonym" && isAnonymExportAllowed && (mobile || !isFullExportAllowed)) {
			return <DefaultComponentMap.OverviewButton {...props} />;
		}

		return null;
	};
}
